import { useFeedback } from "@taunggyi-plus/auth";
import type { FormEvent } from "react";

import { useAuth } from "@taunggyi-plus/auth";
import { BrandLogo, Card, GlobeIcon, RecordFormDialog } from "@taunggyi-plus/design-system";
import type { Locale } from "@taunggyi-plus/domain";

const localeLabels: Record<Locale, string> = {
  en: "English",
  my: "မြန်မာ",
  shn: "တႆး",
  zh: "中文",
};

type StandardMoreProps = {
  readonly locale: Locale;
  readonly onLocaleChange: (locale: Locale) => void;
  readonly onOpenPreferences: () => void;
};

function MoreBackLink() {
  return (
    <a className="more-back-link" href="/more">
      ← More
    </a>
  );
}

export function RoleProfilePage({ roleTitle }: { readonly roleTitle: string }) {
  const auth = useAuth();
  const displayName = String(auth.user?.user_metadata.display_name ?? roleTitle);
  const [contactMessage, setContactMessage] = useFeedback();
  const [passwordMessage, setPasswordMessage] = useFeedback();

  async function changeContact(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const error = await auth.updateContact(
      String(data.get("currentPassword") ?? ""),
      String(data.get("contact") ?? ""),
    );
    setContactMessage(error ?? "Email or phone number updated.");
    if (!error) form.reset();
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const nextPassword = String(data.get("newPassword") ?? "");
    if (nextPassword !== String(data.get("confirmPassword") ?? "")) {
      setPasswordMessage("New passwords do not match.");
      return;
    }
    const error = await auth.updatePassword(
      String(data.get("currentPassword") ?? ""),
      nextPassword,
    );
    setPasswordMessage(error ?? "Password updated.");
    if (!error) form.reset();
  }

  return (
    <div className="role-more-page">
      <MoreBackLink />
      <Card className="more-profile-card role-profile-detail">
        <BrandLogo compact />
        <div>
          <h2>{displayName}</h2>
          <p>{auth.user?.email ?? auth.user?.phone ?? "Approved TaungGyi Link account"}</p>
          {displayName !== roleTitle && <small>{roleTitle}</small>}
        </div>
      </Card>
      <div className="role-profile-settings">
        <div className="profile-setting-row">
          <strong>Name and username</strong>
          <RecordFormDialog title="Edit name and username">
            <form
              className="form-grid"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const values = new FormData(form);
                const error = await auth.updateProfile(
                  String(values.get("name")),
                  String(values.get("username")),
                );
                setContactMessage(error ?? "Profile updated.");
                if (!error) form.closest("dialog")?.close();
              }}
            >
              <label className="field">
                Name
                <input name="name" defaultValue={displayName} maxLength={100} required />
              </label>
              <label className="field">
                Username
                <input
                  name="username"
                  defaultValue={String(auth.user?.user_metadata.username ?? "")}
                  pattern="[a-zA-Z0-9_]{3,40}"
                  required
                />
              </label>
              <button className="button">Save profile</button>
            </form>
          </RecordFormDialog>
        </div>
        <div className="profile-setting-row">
          <strong>Change email or phone number</strong>
          <RecordFormDialog title="Change email or phone number">
            <form className="form-grid" onSubmit={changeContact}>
              <label className="field">
                <span>New email or phone number</span>
                <input name="contact" autoComplete="username" required />
              </label>
              <label className="field">
                <span>Current password</span>
                <input
                  name="currentPassword"
                  type="password"
                  autoComplete="current-password"
                  required
                />
              </label>
              {contactMessage && <p className="form-message">{contactMessage}</p>}
              <button className="button" type="submit">
                Update contact
              </button>
            </form>
          </RecordFormDialog>
        </div>
        <div className="profile-setting-row">
          <strong>Change password</strong>
          <RecordFormDialog title="Change password">
            <form className="form-grid" onSubmit={changePassword}>
              <label className="field">
                <span>Current password</span>
                <input
                  name="currentPassword"
                  type="password"
                  autoComplete="current-password"
                  required
                />
              </label>
              <label className="field">
                <span>New password</span>
                <input
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  required
                />
              </label>
              <label className="field">
                <span>Confirm new password</span>
                <input
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  required
                />
              </label>
              {passwordMessage && <p className="form-message">{passwordMessage}</p>}
              <button className="button" type="submit">
                Update password
              </button>
            </form>
          </RecordFormDialog>
        </div>
      </div>
    </div>
  );
}

function LanguagePage({
  locale,
  onLocaleChange,
}: Pick<StandardMoreProps, "locale" | "onLocaleChange">) {
  return (
    <div className="role-more-page">
      <MoreBackLink />
      <Card className="management-card role-more-detail-card">
        <label className="field">
          <span>Application language</span>
          <select value={locale} onChange={(event) => onLocaleChange(event.target.value as Locale)}>
            {Object.entries(localeLabels).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </Card>
    </div>
  );
}

function LegalSupportPage() {
  return (
    <div className="role-more-page">
      <MoreBackLink />
      <div className="role-more-legal-grid">
        <Card className="management-card role-more-detail-card">
          <h2>Terms & conditions</h2>
          <p>
            Use accurate account and operational information. Service actions remain subject to role
            permissions and audit controls.
          </p>
        </Card>
        <Card className="management-card role-more-detail-card">
          <h2>Privacy & safety</h2>
          <p>
            Customer, partner, driver, rider, order, and financial information must only be used to
            provide and protect the requested service.
          </p>
        </Card>
        <Card className="management-card role-more-detail-card">
          <h2>Contact & support</h2>
          <p>
            Keep the relevant order or record reference ready when contacting TaungGyi Link support.
          </p>
        </Card>
      </div>
    </div>
  );
}

function CustomerTipsPage() {
  return (
    <div className="role-more-page">
      <MoreBackLink />
      <Card className="management-card role-more-detail-card">
        <h2>Getting started</h2>
        <ol>
          <li>Choose a service from Home or Services. Use search to find a provider.</li>
          <li>Sign in or create an account when prompted to book or order.</li>
          <li>Review the provider details, price, and availability before submitting a request.</li>
        </ol>
      </Card>
      <Card className="management-card role-more-detail-card">
        <h2>Choose your Link service</h2>
        <ul>
          <li>
            <strong>Link Stay:</strong> Browse rooms and submit a booking request. Wait for the
            property to confirm.
          </li>
          <li>
            <strong>Link Food:</strong> Choose a restaurant, add food, and check your order and
            delivery details.
          </li>
          <li>
            <strong>Link Delivery:</strong> Browse delivery providers and follow their request
            instructions.
          </li>
          <li>
            <strong>Link Ride:</strong> Browse verified drivers and call in the app to arrange a
            ride.
          </li>
          <li>
            <strong>Link Wash and Link Night:</strong> Coming soon.
          </li>
        </ul>
      </Card>
      <Card className="management-card role-more-detail-card">
        <h2>Track orders and get help</h2>
        <p>
          Open Orders to check your bookings and food orders. Tap the filter icon beside Orders to
          filter by service or status, then select Apply filters.
        </p>
        <p>
          Pay cash directly to the provider. If a cancellation option is available, you can cancel
          from the order details.
        </p>
        <p>For help, keep your booking or order reference ready and call customer support.</p>
        <a href="tel:+959798392286">Customer support: +959798392286</a>
      </Card>
    </div>
  );
}

function TaxiTipsPage() {
  return (
    <div className="role-more-page">
      <MoreBackLink />
      <Card>
        <h2>Drive with Link Ride</h2>
        <p>
          Sign in with the driver account created by Admin. Open My vehicle to check your profile
          and set your availability.
        </p>
      </Card>
      <Card>
        <h2>Handle ride requests</h2>
        <ol>
          <li>Open Orders to review the pickup and destination.</li>
          <li>
            Answer customer voice calls inside the taxi app. Keep the app open to receive calls.
          </li>
          <li>Agree the fare with the customer, then accept the request and enter that fare.</li>
          <li>Start the ride and mark it completed when finished.</li>
        </ol>
      </Card>
      <Card>
        <h2>Your account</h2>
        <p>
          Admin manages your driver photo, vehicle information, verification and subscription.
          Contact Admin when these details change.
        </p>
      </Card>
    </div>
  );
}

function StandardRoleMore({
  locale,
  onLocaleChange,
  roleTitle,
  customer = false,
  taxi = false,
}: StandardMoreProps & {
  readonly roleTitle: string;
  readonly customer?: boolean;
  readonly taxi?: boolean;
}) {
  const page = window.location.pathname.replace(/\/+$/, "");
  if (page === "/more/profile") return <RoleProfilePage roleTitle={roleTitle} />;
  if (customer && page === "/more/how-to-use") return <CustomerTipsPage />;
  if (taxi && page === "/more/how-to-use-taxi") return <TaxiTipsPage />;
  if (!customer && !taxi && page === "/more/language")
    return <LanguagePage locale={locale} onLocaleChange={onLocaleChange} />;
  if (page === "/more/legal-support") return <LegalSupportPage />;

  const links = [
    ...(customer
      ? [
          ["/more/favourites", "Favourites", "Saved providers", "♡"],
          ["/more/reviews", "Reviews", "Your service reviews", "★"],
        ]
      : []),
    ["/more/profile", "Profile", "Account identity and role information", "profile"],
    ...(taxi
      ? [["/drivers", "My vehicle", "Your vehicle details and availability", "profile"]]
      : []),
    ...(customer || taxi
      ? [
          taxi
            ? [
                "/more/how-to-use-taxi",
                "How to use the taxi app",
                "Tips for drivers, ride orders, and finances",
                "?",
              ]
            : [
                "/more/how-to-use",
                "How to use Link",
                "Tips for services, bookings, and orders",
                "?",
              ],
        ]
      : [["/more/language", "Language", localeLabels[locale], "globe"]]),
  ] as const;
  return (
    <div className="customer-more role-more-menu">
      <Card className="more-profile-card">
        <BrandLogo compact />
        <div>
          <strong>{roleTitle}</strong>
        </div>
      </Card>
      <section className="more-group">
        <h2>Account & application</h2>
        <div className="more-menu-list">
          {links.map(([href, title, , icon]) => (
            <a className="more-menu-row" href={href} key={href}>
              <span className="more-menu-row__icon" aria-hidden="true">
                {icon === "globe" ? (
                  <GlobeIcon />
                ) : icon === "profile" || icon === "shield" ? (
                  <svg
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {icon === "profile" ? (
                      <>
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 21v-2a8 8 0 0 1 16 0v2" />
                      </>
                    ) : (
                      <>
                        <path d="m12 3 8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Z" />
                        <path d="m8.5 12 2.5 2.5 4.5-5" />
                      </>
                    )}
                  </svg>
                ) : (
                  icon
                )}
              </span>
              <span>
                <strong>{title}</strong>
              </span>
              <b aria-hidden="true">›</b>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

export function TaxiMore(props: StandardMoreProps) {
  return <StandardRoleMore {...props} roleTitle="Taxi Driver" taxi />;
}

export function CustomerRoleMore(props: StandardMoreProps) {
  return <StandardRoleMore {...props} roleTitle="Customer" customer />;
}

export function HotelMore(props: StandardMoreProps) {
  return <StandardRoleMore {...props} roleTitle="Link Stay Partner" />;
}

export function MerchantMore(props: StandardMoreProps) {
  return <StandardRoleMore {...props} roleTitle="Link Food Partner" />;
}

export function DeliveryOwnerMore(props: StandardMoreProps) {
  return <StandardRoleMore {...props} roleTitle="Delivery Owner" />;
}

export function DeliveryRiderMore(props: StandardMoreProps) {
  return <StandardRoleMore {...props} roleTitle="Delivery Rider" />;
}
